// =============================================================================
// SIREPPAR - Bloques gestionados al final del cuerpo de un informe
// =============================================================================
// Los modales de informe agregan al final del cuerpo bloques que el usuario NO
// redacta a mano: los atributos objetados (checklist) y los antecedentes
// (intimaciones/observaciones tildadas en el panel lateral). Cada bloque se
// reconstruye entero en cada cambio de selección (idempotente) y lo que el
// usuario escribió arriba nunca se pisa.
//
// Antes cada util truncaba el cuerpo en SU marcador, así que si un modal usaba
// los dos bloques a la vez, reconstruir uno borraba el otro. Acá se parsea el
// cuerpo en "base + bloques conocidos" y se reemite todo en un orden fijo, así
// los bloques conviven.
// =============================================================================

export const MARKER_OBJETADOS = '**Atributos objetados:**';
export const MARKER_VERIFICACION = '**Estado de la verificación documental:**';
export const MARKER_ANTECEDENTES = '**Antecedentes:**';

// Orden en que se emiten los bloques al final del cuerpo, más allá del orden en
// que el usuario los haya ido tildando.
const MARKERS_ORDEN = [MARKER_OBJETADOS, MARKER_VERIFICACION, MARKER_ANTECEDENTES];

/**
 * Separa el cuerpo en el texto libre (base) y los bloques gestionados que ya
 * estén presentes, indexados por su marcador.
 */
function partir(cuerpoActual: string): { base: string; bloques: Record<string, string> } {
  const encontrados = MARKERS_ORDEN
    .map((marker) => ({ marker, idx: cuerpoActual.indexOf(marker) }))
    .filter((m) => m.idx >= 0)
    .sort((a, b) => a.idx - b.idx);

  const corte = encontrados.length ? encontrados[0].idx : cuerpoActual.length;
  const base = cuerpoActual.slice(0, corte).replace(/\s+$/, '');

  const bloques: Record<string, string> = {};
  encontrados.forEach(({ marker, idx }, i) => {
    const fin = i + 1 < encontrados.length ? encontrados[i + 1].idx : cuerpoActual.length;
    bloques[marker] = cuerpoActual.slice(idx, fin).replace(/\s+$/, '');
  });

  return { base, bloques };
}

/**
 * Reemplaza (o quita, si `bloque` viene vacío) el bloque gestionado de `marker`
 * y devuelve el cuerpo completo con todos los bloques en el orden canónico.
 */
export function aplicarBloqueGestionado(cuerpoActual: string, marker: string, bloque: string): string {
  const { base, bloques } = partir(cuerpoActual);
  if (bloque) {
    bloques[marker] = bloque;
  } else {
    delete bloques[marker];
  }
  const partes = MARKERS_ORDEN.map((m) => bloques[m]).filter(Boolean);
  return [base, ...partes].filter(Boolean).join('\n\n');
}
